import {
  TouchpointDTOBatchJobExecutionStatus,
  type TouchpointDTOBatchJobExecutionStatus as SyncStatusType,
} from "generated/schemas";

import { type ChipProps } from "ui/chip";

import { TouchpointType } from "types/touchpoint";

export type BatchJobBadgeVariant = "monday" | "email-writer";

type SyncBadgeConfig = {
  /** `violet` was the email-writer colour until the design system dropped that family. */
  color: Extract<ChipProps["color"], "error" | "default" | "info">;
  label: string;
  variant: BatchJobBadgeVariant;
};

const MONDAY_SYNC_STATUS_BADGE: Partial<Record<SyncStatusType, Omit<SyncBadgeConfig, "variant">>> = {
  [TouchpointDTOBatchJobExecutionStatus.FAILED]: { color: "error", label: "Sync failed" },
  [TouchpointDTOBatchJobExecutionStatus.RUNNING]: { color: "default", label: "Syncing..." },
};

const EMAIL_WRITER_SYNC_STATUS_BADGE: Partial<
  Record<SyncStatusType, Omit<SyncBadgeConfig, "variant">>
> = {
  [TouchpointDTOBatchJobExecutionStatus.FAILED]: { color: "error", label: "Writing failed" },
  [TouchpointDTOBatchJobExecutionStatus.RUNNING]: { color: "info", label: "Writing..." },
};

export const getBatchJobBadgeVariant = (
  touchpoint: { type: string; latestVersion?: { aiGenerated?: boolean; version?: number } },
  isTemplateCampaign: boolean,
  options?: { isEmailWriterSessionActive?: boolean },
): BatchJobBadgeVariant => {
  // A rewrite creates its AI-generated version (always a successor, never v1) before the job
  // starts, so the row identifies the job even when this tab holds no email-writer session
  // (another tab, reload, cleared on failure). Monday creates v1 only, possibly cloned from an
  // AI-written program template, and runs only on newly created touchpoints, never after a rewrite.
  const isRewriteVersion =
    Boolean(touchpoint.latestVersion?.aiGenerated) && (touchpoint.latestVersion?.version ?? 0) > 1;
  const isEmailWriterContext =
    isTemplateCampaign || Boolean(options?.isEmailWriterSessionActive) || isRewriteVersion;

  if (isEmailWriterContext && touchpoint.type === TouchpointType.EMAIL) {
    return "email-writer";
  }

  return "monday";
};

export const getSyncBadgeConfig = (
  status: SyncStatusType | undefined,
  variant: BatchJobBadgeVariant = "monday",
): SyncBadgeConfig | null => {
  if (!status || status === TouchpointDTOBatchJobExecutionStatus.SUCCESSFUL) {
    return null;
  }

  const badgeByVariant =
    variant === "email-writer" ? EMAIL_WRITER_SYNC_STATUS_BADGE : MONDAY_SYNC_STATUS_BADGE;
  const badge = badgeByVariant[status];

  if (!badge) {
    return null;
  }

  return { ...badge, variant };
};
